import { Button, Form } from '@edx/paragon';
import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback } from 'react';
import ArrowRight from '../assets/ai/ArrowRight';

const ChatInput = ({ className, mutate }) => {
  const [input, setInput] = useState('');
  const textAreaElementRef = useRef(null);

  const setTextAreaRef = useCallback(node => {
    if (node) {
      if (node.tagName === 'TEXTAREA') {
        textAreaElementRef.current = node;
      } else if (node.querySelector) {
        textAreaElementRef.current = node.querySelector('textarea');
      }
    }
  }, []);

  const adjustHeight = useCallback(() => {
    const textAreaElement = textAreaElementRef.current;
    if (!textAreaElement) return;

    // Get the parent container height (AI window)
    const parentContainer = textAreaElement.closest('.ai-tutor > div');

    const minHeight = 50;

    let maxHeight = 128;
    if (parentContainer) {
      const calculatedMax = parentContainer.offsetHeight * 0.3;
      maxHeight = Math.max(calculatedMax, 128);
    }

    textAreaElement.style.height = 'auto';
    const scrollHeight = textAreaElement.scrollHeight;
    const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));

    textAreaElement.style.height = `${newHeight}px`;
    textAreaElement.style.maxHeight = `${maxHeight}px`;
    textAreaElement.style.minHeight = `${minHeight}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [input, adjustHeight]);

  // Handle window resize
  useEffect(() => {
    let resizeTimeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(adjustHeight, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => {
      setTimeout(adjustHeight, 200);
    });

    // Also observe parent container size changes
    const textAreaElement = textAreaElementRef.current;
    const parentContainer = textAreaElement?.closest('.ai-tutor > div');

    const resizeObserver = new ResizeObserver(handleResize);
    if (parentContainer) {
      resizeObserver.observe(parentContainer);
    }

    setTimeout(adjustHeight, 100);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      resizeObserver.disconnect();
    };
  }, [adjustHeight]);

  const handleChange = e => {
    setInput(e.target.value);
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!input.trim()) {
      return;
    }

    const formData = new FormData(e.target);
    mutate(formData);

    setInput('');
  };

  return (
    <div className={`tw-p-5 ${className}`}>
      <form onSubmit={handleSubmit} className="tw-relative tw-w-full" autoComplete="off">
        <Form.Group>
          <Form.Control
            ref={setTextAreaRef}
            as="textarea"
            name="message"
            value={input}
            onChange={handleChange}
            autoComplete="off"
            placeholder="Напишіть питання"
            rows={1}
            className="tw-mr-0"
            controlClassName="!tw-resize-none !tw-rounded-[10px] !tw-border-gray-500 tw-py-3 tw-pl-5 tw-pr-12"
          />
        </Form.Group>
        <Button
          type="submit"
          variant="brand"
          size="sm"
          className="!tw-absolute tw-bottom-[10px] tw-right-2 tw-h-8 tw-w-8 tw-rounded-full !tw-p-2"
        >
          <ArrowRight />
          <span className="tw-sr-only">Відправити повідомлення ШІ помічнику</span>
        </Button>
      </form>

      <p className="tw-mt-2 tw-text-center tw-text-[12px] tw-leading-none tw-text-gray-200">
        AI Tutor може помилятися. Перевіряйте важливу інформацію.
      </p>
    </div>
  );
};

ChatInput.propTypes = {
  className: PropTypes.string,
  mutate: PropTypes.func.isRequired,
};

ChatInput.defaultProps = {
  className: '',
};

export default ChatInput;
